import type { CreatePluginType, EmblaCarouselType } from 'embla-carousel'
import { theme } from '@lounge-fe/tailwind'

const Stack = (): CreatePluginType<{}, {}> => {
  type Axis = 'x' | 'y'

  let embla: EmblaCarouselType
  let restoreTranslate: (() => void) | null = null
  let lastSelectedIndex = 0

  const DESKTOP_TRANSITION_MS = 300

  let isMobile: boolean =
    typeof window !== 'undefined' &&
    'matchMedia' in window &&
    window.matchMedia(`(max-width: ${theme.screens.md})`).matches

  const currentAxis = (): Axis => {
    return (embla.internalEngine().options.axis as Axis) || 'x'
  }

  let desktopTransitionTimeoutId: ReturnType<typeof setTimeout> | null = null

  const setDesktopTransitioning = (value: boolean) => {
    if (!embla) return
    const root = embla.rootNode()
    if (value) {
      root.dataset.vcTransitioning = 'true'
    } else {
      delete root.dataset.vcTransitioning
    }
  }

  const startDesktopTransitionLock = () => {
    if (!embla) return
    setDesktopTransitioning(true)
    if (desktopTransitionTimeoutId !== null) {
      clearTimeout(desktopTransitionTimeoutId)
    }
    desktopTransitionTimeoutId = setTimeout(() => {
      setDesktopTransitioning(false)
      desktopTransitionTimeoutId = null
    }, DESKTOP_TRANSITION_MS)
  }

  const handleResize = () => {
    isMobile =
      typeof window !== 'undefined' &&
      'matchMedia' in window &&
      window.matchMedia(`(max-width: ${theme.screens.md})`).matches
  }

  const setSlideTransitionDesktop = (enable: boolean) => {
    const value = enable
      ? `transform ${DESKTOP_TRANSITION_MS}ms cubic-bezier(0.44, 0, 0.56, 1)`
      : ''
    embla.slideNodes().forEach((slide) => {
      slide.style.transition = value
    })
  }

  // Disable default container translation so we can control slides ourselves
  const disableTranslate = () => {
    const { translate, slideLooper } = embla.internalEngine()
    translate.clear()
    translate.toggleActive(false)
    slideLooper.loopPoints.forEach(({ translate }) => {
      translate.clear()
      translate.toggleActive(false)
    })
    restoreTranslate = () => {
      translate.toggleActive(true)
      slideLooper.loopPoints.forEach(({ translate }) => {
        translate.toggleActive(true)
      })
    }
  }

  /**
   * Compute the translate percentage for a slide as a continuous function of
   * the fractional carousel index progress (p). This removes the need for CSS
   * transitions by tweening based on scroll position.
   *
   * Rules (for current progress p in [0, snaps-1], s = floor(p)):
   * - If index <= s: position = -100 * index
   * - If index === s + 1: position = -100 * p (tween)
   * - If index > s + 1: position = -100 * (index - 1)
   */
  const computeTranslatePercentForProgress = (
    index: number,
    p: number,
  ): string => {
    const clampedP = Math.max(0, p)
    const s = Math.floor(clampedP)
    if (index <= s) return `-${100 * index}%`
    if (index === s + 1) return `-${100 * clampedP}%`
    return `-${100 * (index - 1)}%`
  }

  const applySlideStyles = (
    slide: HTMLElement,
    index: number,
    p: number,
    axis: Axis,
  ) => {
    const translate = computeTranslatePercentForProgress(index, p)
    slide.style.transform = `translate${axis === 'x' ? 'X' : 'Y'}(${translate})`
    slide.style.zIndex = String(index)
  }

  const getIndexProgress = (): number => {
    const steps = embla.scrollSnapList().length - 1
    if (steps <= 0) return 0
    const progress = embla.scrollProgress()
    return Math.max(0, Math.min(1, progress)) * steps
  }

  const renderAtProgress = (p: number) => {
    const axis = currentAxis()
    embla.slideNodes().forEach((slide, index) => {
      applySlideStyles(slide as HTMLElement, index, p, axis)
    })
  }

  const handleSelect = () => {
    if (isMobile) {
      setSlideTransitionDesktop(false)
      renderAtProgress(getIndexProgress())
    } else {
      setSlideTransitionDesktop(true)
      renderAtProgress(embla.selectedScrollSnap())
      startDesktopTransitionLock()
    }
    lastSelectedIndex = embla.selectedScrollSnap()
  }

  const handleReInit = () => {
    // Embla may re-enable its own translate on reInit; ensure it's disabled again
    disableTranslate()
    lastSelectedIndex = embla.selectedScrollSnap()
    setSlideTransitionDesktop(false)
    renderAtProgress(isMobile ? getIndexProgress() : lastSelectedIndex)
  }

  const handleScroll = () => {
    if (isMobile) {
      setSlideTransitionDesktop(false)
      renderAtProgress(getIndexProgress())
    }
  }

  const init = (emblaApi: EmblaCarouselType) => {
    embla = emblaApi
    disableTranslate()
    lastSelectedIndex = embla.selectedScrollSnap()
    setSlideTransitionDesktop(false)
    renderAtProgress(isMobile ? getIndexProgress() : lastSelectedIndex)

    embla.on('select', handleSelect)
    embla.on('reInit', handleReInit) // keep correct positions on reInit/resize
    embla.on('scroll', handleScroll)
    embla.on('resize', handleResize)

    if (embla?.rootNode()) {
      embla.rootNode().style.overscrollBehavior = 'none'
    }
  }

  const destroy = () => {
    if (desktopTransitionTimeoutId !== null) {
      clearTimeout(desktopTransitionTimeoutId)
      desktopTransitionTimeoutId = null
    }
    setDesktopTransitioning(false)
    embla.off('select', handleSelect)
    embla.off('reInit', handleReInit)
    embla.off('scroll', handleScroll)
    embla.off('resize', handleResize)
    if (restoreTranslate) restoreTranslate()
    embla.slideNodes().forEach((slide) => {
      slide.style.transform = ''
      slide.style.zIndex = ''
      slide.style.transition = ''
      slide.style.willChange = ''
    })

    if (embla?.rootNode()) {
      embla.rootNode().style.overscrollBehavior = ''
    }
  }

  return {
    name: 'stack',
    options: {},
    init,
    destroy,
  }
}

export default Stack
